/**
 * Catalog Service - CRUD operations for Offerings Catalog
 * 
 * Provides database-backed catalog management with:
 * - Full CRUD for products, variants, images, vendors, SKUs, mapping rules
 * - CSV import with merge (upsert) strategy
 * - CSV export functionality
 * - Supabase Storage integration for images
 */

import { supabase } from '@/lib/supabase';
import type {
    CatalogCategory, CatalogCategoryCreate, CatalogCategoryUpdate,
    CatalogVendor, CatalogVendorCreate, CatalogVendorUpdate,
    CatalogProduct, CatalogProductCreate, CatalogProductUpdate,
    CatalogVariant, CatalogVariantCreate, CatalogVariantUpdate,
    CatalogImage, CatalogImageCreate, CatalogImageUpdate,
    CatalogProductVendor, CatalogProductVendorCreate, CatalogProductVendorUpdate,
    CatalogSku, CatalogSkuCreate, CatalogSkuUpdate,
    CatalogSkuMappingRule, CatalogSkuMappingRuleCreate, CatalogSkuMappingRuleUpdate,
    CsvProductImport, CsvImportResult
} from '@/types/database';

const CATALOG_READ_TTL_MS = 30_000;
const catalogReadCache = new Map<string, { at: number; value: unknown }>();
const catalogReadInFlight = new Map<string, Promise<unknown>>();

function invalidateCatalogReadCache(prefixes: string[]) {
    for (const key of Array.from(catalogReadCache.keys())) {
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
            catalogReadCache.delete(key);
        }
    }
    for (const key of Array.from(catalogReadInFlight.keys())) {
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
            catalogReadInFlight.delete(key);
        }
    }
}

async function readThroughCatalogCache<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const cached = catalogReadCache.get(key);
    if (cached && now - cached.at < CATALOG_READ_TTL_MS) {
        return cached.value as T;
    }

    const existing = catalogReadInFlight.get(key);
    if (existing) {
        return existing as Promise<T>;
    }

    const request = (async () => {
        const value = await fetcher();
        catalogReadCache.set(key, { at: Date.now(), value });
        return value;
    })();

    catalogReadInFlight.set(key, request);
    try {
        return await request;
    } finally {
        catalogReadInFlight.delete(key);
    }
}

/* ══════════════════════════════════════════════════════════
   Categories
   ══════════════════════════════════════════════════════════ */

export async function getCategories(): Promise<CatalogCategory[]> {
    const { data, error } = await supabase
        .from('catalog_categories')
        .select('*')
        .order('sort_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
}

export async function getCategoryById(id: string): Promise<CatalogCategory | null> {
    const { data, error } = await supabase
        .from('catalog_categories')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) throw error;
    return data;
}

export async function createCategory(input: CatalogCategoryCreate): Promise<CatalogCategory> {
    const { data, error } = await supabase  
        .from('catalog_categories')
        .insert(input)
        .select()
        .single();

    if (error) throw error;
    return data;
}

export async function updateCategory(id: string, input: CatalogCategoryUpdate): Promise<CatalogCategory> {
    const { data, error } = await supabase
        .from('catalog_categories')
        .update(input)
        .eq('id', id)
        .select()
        .single();

    if (error) throw error;
    return data;
}

export async function deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
        .from('catalog_categories')
        .delete()
        .eq('id', id);

    if (error) throw error;
}



/* ══════════════════════════════════════════════════════════
   Vendors
   ══════════════════════════════════════════════════════════ */

export async function getVendors(): Promise<CatalogVendor[]> {
    const { data, error } = await supabase
        .from('catalog_vendors')
        .select('*')
        .order('name', { ascending: true });
    
    if (error) throw error;
    return data || [];
}

export async function getVendorById(id: string): Promise<CatalogVendor | null> {
    const { data, error } = await supabase
        .from('catalog_vendors')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) throw error;
    return data;
}

export async function getVendorByCode(code: string): Promise<CatalogVendor | null> {
    const { data, error } = await supabase
        .from('catalog_vendors')
        .select('*')
        .eq('code', code)
        .single();
    
    if (error) throw error;
    return data;
}

export async function createVendor(input: CatalogVendorCreate): Promise<CatalogVendor> {
    const { data, error } = await supabase
        .from('catalog_vendors')
        .insert(input)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function updateVendor(id: string, input: CatalogVendorUpdate): Promise<CatalogVendor> {
    const { data, error } = await supabase
        .from('catalog_vendors')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function deleteVendor(id: string): Promise<void> {
    const { error } = await supabase
        .from('catalog_vendors')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
}

/* ══════════════════════════════════════════════════════════
   Products
   ══════════════════════════════════════════════════════════ */

export async function getProducts(options?: {
    categoryId?: string;
    activeOnly?: boolean;
    search?: string;
}): Promise<CatalogProduct[]> {
    let query = supabase
        .from('catalog_products')
        .select('*')
        .order('name', { ascending: true });

    if (options?.categoryId) {
        query = query.eq('category_id', options.categoryId);
    }
    if (options?.activeOnly) {
        query = query.eq('is_active', true);
    }
    if (options?.search) {
        query = query.or(`name.ilike.%${options.search}%,sku.ilike.%${options.search}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
}

export async function getProductById(id: string): Promise<CatalogProduct | null> {
    const { data, error } = await supabase
        .from('catalog_products')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) throw error;
    return data;
}

export async function createProduct(input: CatalogProductCreate): Promise<CatalogProduct> {
    const { data, error } = await supabase
        .from('catalog_products')
        .insert(input)
        .select()
        .single();
    
    if (error) {
        console.error("createProduct error:", error);
        throw error;
    }
    if (!data) {
        throw new Error("Failed to create product");
    }
    return data;
}

export async function updateProduct(id: string, input: CatalogProductUpdate): Promise<CatalogProduct> {
    const { data, error } = await supabase
        .from('catalog_products')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) {
        console.error("updateProduct error:", error);
        throw error;
    }
    if (!data) {
        throw new Error(`Product not found: ${id}`);
    }
    return data;
}

export async function deleteProduct(id: string): Promise<void> {
    // First try to update to set is_active = false (soft delete)
    const { error: updateError } = await supabase
        .from('catalog_products')
        .update({ is_active: false })
        .eq('id', id);
    
    if (updateError) {
        console.error("deleteProduct (soft) error:", updateError);
        // If soft delete fails, try hard delete
        const { error } = await supabase
            .from('catalog_products')
            .delete()
            .eq('id', id);
        
        if (error) {
            console.error("deleteProduct (hard) error:", error);
            throw error;
        }
        return;
    }
}

/* ══════════════════════════════════════════════════════════
   Variants
   ══════════════════════════════════════════════════════════ */

export async function getVariantsByProduct(productId: string): Promise<CatalogVariant[]> {
    return readThroughCatalogCache(`variants:product:${productId}`, async () => {
        const { data, error } = await supabase
            .from('catalog_variants')
            .select('*')
            .eq('product_id', productId)
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    });
}

export async function getVariants(): Promise<CatalogVariant[]> {
    return readThroughCatalogCache('variants:all', async () => {
        const { data, error } = await supabase
            .from('catalog_variants')
            .select('*')
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    });
}

export async function getVariantById(id: string): Promise<CatalogVariant | null> {
    const { data, error } = await supabase
        .from('catalog_variants')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) throw error;
    return data;
}

export async function createVariant(input: CatalogVariantCreate): Promise<CatalogVariant> {
    const { data, error } = await supabase
        .from('catalog_variants')
        .insert(input)
        .select()
        .single();
    
    if (error) {
        console.error("createVariant error:", error);
        throw error;
    }
    if (!data) {
        throw new Error("Failed to create variant");
    }
    invalidateCatalogReadCache([
        'variants:all',
        `variants:product:${data.product_id}`,
        'images:all',
        `images:variant:${data.id}`,
    ]);
    return data;
}

export async function updateVariant(id: string, input: CatalogVariantUpdate): Promise<CatalogVariant> {
    const { data, error } = await supabase
        .from('catalog_variants')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) {
        console.error("updateVariant error:", error);
        throw error;
    }
    if (!data) {
        throw new Error(`Variant not found: ${id}`);
    }
    invalidateCatalogReadCache([
        'variants:all',
        `variants:product:${data.product_id}`,
        'images:all',
        `images:variant:${data.id}`,
    ]);
    return data;
}

export async function deleteVariant(id: string): Promise<void> {
    const variant = await getVariantById(id).catch(() => null);
    const productId = variant?.product_id ?? null;
    // Try soft delete first (set is_active = false)
    const { error: updateError } = await supabase
        .from('catalog_variants')
        .update({ is_active: false })
        .eq('id', id);
    
    if (updateError) {
        console.error("deleteVariant (soft) error:", updateError);
        // If soft delete fails, try hard delete
        const { error } = await supabase
            .from('catalog_variants')
            .delete()
            .eq('id', id);
        
        if (error) {
            console.error("deleteVariant (hard) error:", error);
            throw error;
        }
    }
    invalidateCatalogReadCache([
        'variants:all',
        productId ? `variants:product:${productId}` : "variants:product:",
        'images:all',
        `images:variant:${id}`,
    ]);
}

/* ══════════════════════════════════════════════════════════
   Images
   ══════════════════════════════════════════════════════════ */

export async function getImagesByProduct(productId: string): Promise<CatalogImage[]> {
    return readThroughCatalogCache(`images:product:${productId}`, async () => {
        const { data, error } = await supabase
            .from('catalog_images')
            .select('*')
            .eq('product_id', productId)
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    });
}

export async function getImagesByVariant(variantId: string): Promise<CatalogImage[]> {
    return readThroughCatalogCache(`images:variant:${variantId}`, async () => {
        const { data, error } = await supabase
            .from('catalog_images')
            .select('*')
            .eq('variant_id', variantId)
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    });
}

export async function getImages(): Promise<CatalogImage[]> {
    return readThroughCatalogCache('images:all', async () => {
        const { data, error } = await supabase
            .from('catalog_images')
            .select('*')
            .order('sort_order', { ascending: true });

        if (error) throw error;
        return data || [];
    });
}

export async function createImage(input: CatalogImageCreate): Promise<CatalogImage> {
    const { data, error } = await supabase
        .from('catalog_images')
        .insert(input)
        .select()
        .single();
    
    if (error) throw error;
    if (data) {
        invalidateCatalogReadCache([
            "images:all",
            data.product_id ? `images:product:${data.product_id}` : "images:product:",
            data.variant_id ? `images:variant:${data.variant_id}` : "images:variant:",
        ]);
    }
    return data;
}

export async function updateImage(id: string, input: CatalogImageUpdate): Promise<CatalogImage> {
    const existing = await supabase
        .from('catalog_images')
        .select('product_id,variant_id')
        .eq('id', id)
        .maybeSingle();

    const { data, error } = await supabase
        .from('catalog_images')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) throw error;
    invalidateCatalogReadCache([
        "images:all",
        existing.data?.product_id ? `images:product:${existing.data.product_id}` : "images:product:",
        existing.data?.variant_id ? `images:variant:${existing.data.variant_id}` : "images:variant:",
        data.product_id ? `images:product:${data.product_id}` : "images:product:",
        data.variant_id ? `images:variant:${data.variant_id}` : "images:variant:",
    ]);
    return data;
}

export async function deleteImage(id: string): Promise<void> {
    const existing = await supabase
        .from('catalog_images')
        .select('product_id,variant_id')
        .eq('id', id)
        .maybeSingle();

    const { error } = await supabase
        .from('catalog_images')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
    invalidateCatalogReadCache([
        "images:all",
        existing.data?.product_id ? `images:product:${existing.data.product_id}` : "images:product:",
        existing.data?.variant_id ? `images:variant:${existing.data.variant_id}` : "images:variant:",
    ]);
}

/* ══════════════════════════════════════════════════════════
   Product-Vendor Links
   ══════════════════════════════════════════════════════════ */

export async function getProductVendorsByProduct(productId: string): Promise<CatalogProductVendor[]> {
    const { data, error } = await supabase
        .from('catalog_product_vendors')
        .select('*')
        .eq('product_id', productId);
    
    if (error) throw error;
    return data || [];
}

export async function createProductVendor(input: CatalogProductVendorCreate): Promise<CatalogProductVendor> {
    const { data, error } = await supabase
        .from('catalog_product_vendors')
        .insert(input)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function updateProductVendor(id: string, input: CatalogProductVendorUpdate): Promise<CatalogProductVendor> {
    const { data, error } = await supabase
        .from('catalog_product_vendors')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function deleteProductVendor(id: string): Promise<void> {
    const { error } = await supabase
        .from('catalog_product_vendors')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
}

/* ══════════════════════════════════════════════════════════
   SKUs
   ══════════════════════════════════════════════════════════ */

export async function getSkus(options?: {
    category?: string;
    activeOnly?: boolean;
}): Promise<CatalogSku[]> {
    let query = supabase
        .from('catalog_skus')
        .select('*')
        .order('name', { ascending: true });

    if (options?.category) {
        query = query.eq('category', options.category);
    }
    if (options?.activeOnly) {
        query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
}

export async function getSkuById(id: string): Promise<CatalogSku | null> {
    const { data, error } = await supabase
        .from('catalog_skus')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) throw error;
    return data;
}

export async function getSkuByCode(sku: string): Promise<CatalogSku | null> {
    const { data, error } = await supabase
        .from('catalog_skus')
        .select('*')
        .eq('sku', sku)
        .single();
    
    if (error) throw error;
    return data;
}

export async function createSku(input: CatalogSkuCreate): Promise<CatalogSku> {
    const { data, error } = await supabase
        .from('catalog_skus')
        .insert(input)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function updateSku(id: string, input: CatalogSkuUpdate): Promise<CatalogSku> {
    const { data, error } = await supabase
        .from('catalog_skus')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function deleteSku(id: string): Promise<void> {
    const { error } = await supabase
        .from('catalog_skus')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
}

/* ══════════════════════════════════════════════════════════
   SKU Mapping Rules
   ══════════════════════════════════════════════════════════ */

export async function getSkuMappingRules(options?: {
    activeOnly?: boolean;
}): Promise<CatalogSkuMappingRule[]> {
    let query = supabase
        .from('catalog_sku_mapping_rules')
        .select('*')
        .order('priority', { ascending: false });

    if (options?.activeOnly) {
        query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
}

export async function createSkuMappingRule(input: CatalogSkuMappingRuleCreate): Promise<CatalogSkuMappingRule> {
    const { data, error } = await supabase
        .from('catalog_sku_mapping_rules')
        .insert(input)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function updateSkuMappingRule(id: string, input: CatalogSkuMappingRuleUpdate): Promise<CatalogSkuMappingRule> {
    const { data, error } = await supabase
        .from('catalog_sku_mapping_rules')
        .update(input)
        .eq('id', id)
        .select()
        .single();
    
    if (error) throw error;
    return data;
}

export async function deleteSkuMappingRule(id: string): Promise<void> {
    const { error } = await supabase
        .from('catalog_sku_mapping_rules')
        .delete()
        .eq('id', id);
    
    if (error) throw error;
}

/* ══════════════════════════════════════════════════════════
   CSV Import/Export
   ══════════════════════════════════════════════════════════ */

/**
 * Import SKUs from CSV with merge strategy (upsert by SKU)
 * Updates existing records, adds new ones
 */
export async function importSkusFromCsv(records: CsvProductImport[]): Promise<CsvImportResult> {
    const errors: string[] = [];
    let imported = 0;
    let updated = 0;

    // Get all existing vendors for mapping
    const vendors = await getVendors();
    const vendorMap = new Map(vendors.map(v => [v.code.toLowerCase(), v.id]));

    for (const record of records) {
        try {
            // Validate required fields
            if (!record.sku || !record.name) {
                errors.push(`Missing required fields (sku, name) for record: ${JSON.stringify(record)}`);
                continue;
            }

            // Check if SKU exists
            const existingSku = await getSkuByCode(record.sku);

            if (existingSku) {
                // Update existing
                const updateData: CatalogSkuUpdate = {
                    name: record.name,
                    description: record.description || null,
                    category: record.category || null,
                    unit_cost: record.unit_cost ?? existingSku.unit_cost,
                    unit_price: record.unit_price ?? existingSku.unit_price,
                    is_active: record.is_active ?? existingSku.is_active,
                };

                // Handle vendor mapping if vendor code provided
                if (record.vendor) {
                    const vendorId = vendorMap.get(record.vendor.toLowerCase());
                    if (vendorId) {
                        updateData.primary_vendor_id = vendorId;
                    }
                }

                await updateSku(existingSku.id, updateData);
                updated++;
            } else {
                // Create new
                const createData = {
                    sku: record.sku,
                    name: record.name,
                    description: record.description || null,
                    category: record.category || null,
                    unit_cost: record.unit_cost ?? null,
                    unit_price: record.unit_price ?? null,
                    unit_of_measure: 'unit',
                    is_active: record.is_active ?? true,
                    weight_kg: null,
                    primary_vendor_id: null,
                    alternate_vendor_id: null,
                    external_item_id: null,
                } as CatalogSkuCreate;

                // Handle vendor mapping if vendor code provided
                if (record.vendor) {
                    const vendorId = vendorMap.get(record.vendor.toLowerCase());
                    if (vendorId) {
                        createData.primary_vendor_id = vendorId;
                    }
                }

                await createSku(createData);
                imported++;
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            errors.push(`Failed to import SKU ${record.sku}: ${errorMessage}`);
        }
    }

    return {
        success: errors.length === 0,
        imported,
        updated,
        errors
    };
}

/**
 * Export SKUs to CSV format
 */
export async function exportSkusToCsv(): Promise<string> {
    const skus = await getSkus({ activeOnly: true });
    
    // Get vendors for mapping
    const vendors = await getVendors();
    const vendorMap = new Map(vendors.map(v => [v.id, v.code]));

    const headers = ['sku', 'name', 'description', 'category', 'unit_cost', 'unit_price', 'vendor', 'vendor_sku', 'is_active'];
    const rows = skus.map(sku => {
        const vendorCode = sku.primary_vendor_id ? vendorMap.get(sku.primary_vendor_id) || '' : '';
        return [
            sku.sku,
            `"${(sku.name || '').replace(/"/g, '""')}"`,
            `"${(sku.description || '').replace(/"/g, '""')}"`,
            sku.category || '',
            sku.unit_cost?.toString() || '',
            sku.unit_price?.toString() || '',
            vendorCode,
            '',
            sku.is_active ? 'true' : 'false'
        ].join(',');
    });

    return [headers.join(','), ...rows].join('\n');
}

/* ══════════════════════════════════════════════════════════
   Supabase Storage for Images
   ══════════════════════════════════════════════════════════ */

const PRODUCT_IMAGES_BUCKET = 'product-images';

export async function uploadProductImage(
    file: File,
    productId: string,
    options?: { variantId?: string; isPrimary?: boolean }
): Promise<CatalogImage> {
    // Upload to Supabase Storage
    const fileName = `${productId}/${options?.variantId || 'main'}-${Date.now()}-${file.name}`;
    const { data: uploadData, error: uploadError } = await supabase.storage
        .from(PRODUCT_IMAGES_BUCKET)
        .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
        });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
        .from(PRODUCT_IMAGES_BUCKET)
        .getPublicUrl(fileName);

    // Create image record
    const imageData: CatalogImageCreate = {
        product_id: productId,
        variant_id: options?.variantId || null,
        image_url: publicUrl,
        thumbnail_url: publicUrl, // Could generate thumbnail separately
        alt_text: file.name,
        is_primary: options?.isPrimary || false,
        sort_order: 0
    };

    return createImage(imageData);
}

export async function deleteProductImage(imageId: string): Promise<void> {
    // Get image record first
    const { data: image, error: fetchError } = await supabase
        .from('catalog_images')
        .select('image_url')
        .eq('id', imageId)
        .single();

    if (fetchError) throw fetchError;

    // Extract file path from URL
    const urlParts = image.image_url.split('/');
    const filePath = urlParts.slice(-2).join('/'); // bucket-name/uuid/filename

    // Delete from storage
    const { error: storageError } = await supabase.storage
        .from(PRODUCT_IMAGES_BUCKET)
        .remove([filePath]);

    if (storageError) console.error('Failed to delete image from storage:', storageError);

    // Delete record
    await deleteImage(imageId);
}

/* ══════════════════════════════════════════════════════════
   Bulk Operations
   ══════════════════════════════════════════════════════════ */

export async function bulkUpdateSkuActiveStatus(skuIds: string[], isActive: boolean): Promise<void> {
    const { error } = await supabase
        .from('catalog_skus')
        .update({ is_active: isActive })
        .in('id', skuIds);

    if (error) throw error;
}

export async function bulkDeleteSkus(skuIds: string[]): Promise<void> {
    const { error } = await supabase
        .from('catalog_skus')
        .delete()
        .in('id', skuIds);

    if (error) throw error;
}

/* ══════════════════════════════════════════════════════════
   Utility Functions
   ══════════════════════════════════════════════════════════ */

export async function getCatalogStats(): Promise<{
    totalProducts: number;
    activeProducts: number;
    totalSkus: number;
    activeSkus: number;
    totalVendors: number;
    totalCategories: number;
}> {
    const [products, skus, vendors, categories] = await Promise.all([
        supabase.from('catalog_products').select('id', { count: 'exact', head: true }),
        supabase.from('catalog_skus').select('id', { count: 'exact', head: true }),
        supabase.from('catalog_vendors').select('id', { count: 'exact', head: true }),
        supabase.from('catalog_categories').select('id', { count: 'exact', head: true })
    ]);

    const activeProducts = await supabase
        .from('catalog_products')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true);

    const activeSkus = await supabase
        .from('catalog_skus')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true);

    return {
        totalProducts: products.count || 0,
        activeProducts: activeProducts.count || 0,
        totalSkus: skus.count || 0,
        activeSkus: activeSkus.count || 0,
        totalVendors: vendors.count || 0,
        totalCategories: categories.count || 0
    };
}
